import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Prize } from '../lib/types';
import { Countdown } from '../components/Countdown';
import { CoinIcon } from '../components/Currency';
import { Sheet } from '../components/Sheet';
import { nfmt, plural } from '../lib/format';
import { haptic, tg } from '../lib/telegram';
import { useApp } from '../App';

interface Payload {
  prizes: Prize[];
  coins: number;
  drawAt: string;
  frozen: boolean;
}

export function PrizesScreen({ onClose }: { onClose: () => void }) {
  const { refresh, toast, me } = useApp();
  const [data, setData] = useState<Payload | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [needPhone, setNeedPhone] = useState(false);

  const load = () => api.get<Payload>('/prizes').then(setData).catch(() => {});
  useEffect(() => { void load(); }, []);

  async function buy(prize: Prize) {
    haptic.tap();
    setBusy(prize.id);
    try {
      await api.post(`/prizes/${prize.id}/ticket`);
      haptic.ok();
      toast(`Билет на «${prize.title}» твой`);
      await Promise.all([load(), refresh()]);
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'phone_required') setNeedPhone(true);
      else { haptic.no(); toast(err.message, 'bad'); }
    } finally {
      setBusy(null);
    }
  }

  if (!data) return <div className="h-64 animate-pulse rounded-3xl bg-surface" />;

  const coins = data.coins;
  const next = [...data.prizes]
    .filter((p) => p.ticket_price_coins > coins)
    .sort((a, b) => a.ticket_price_coins - b.ticket_price_coins)[0];

  return (
    <div className="rise pb-6">
      <div className="card p-5 text-center">
        <div className="mb-1 flex items-center justify-center gap-2">
          <CoinIcon size={26} />
          <span className="font-display text-4xl leading-none num">{nfmt(coins)}</span>
        </div>
        <p className="label">коинов на билеты</p>

        <div className="mt-4 rounded-xl border border-line bg-raised px-3 py-2.5">
          <p className="text-xs text-ash">
            {data.frozen ? 'Билеты больше не выдаются' : 'До розыгрыша'}
          </p>
          <Countdown to={data.drawAt} className="text-lg font-semibold" />
        </div>

        {next && (
          <div className="mt-4 text-left">
            <div className="mb-1.5 flex items-baseline justify-between text-xs">
              <span className="text-ash">До билета на «{next.title}»</span>
              <span className="num font-semibold">{nfmt(next.ticket_price_coins - coins)}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-line">
              <div className="h-full rounded-full bg-gold transition-[width] duration-500"
                   style={{ width: `${Math.min(100, (coins / next.ticket_price_coins) * 100)}%` }} />
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-3">
        {data.prizes.map((p) => {
          const afford = coins >= p.ticket_price_coins;
          const maxed = p.my_tickets >= p.max_tickets_per_user;
          return (
            <article key={p.id} className="card overflow-hidden">
              {p.image_url && (
                <img src={p.image_url} alt="" className="h-36 w-full object-cover" loading="lazy" />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display text-[15px] leading-snug">{p.title}</h3>
                    {p.description && <p className="mt-1 text-xs text-ash">{p.description}</p>}
                  </div>
                  {p.my_tickets > 0 && (
                    <span className="shrink-0 rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1
                                     text-xs font-bold text-gold num">
                      {p.my_tickets} {plural(p.my_tickets, 'билет', 'билета', 'билетов')}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-ash">
                    {p.participants > 0
                      ? `Участвуют: ${nfmt(p.participants)}`
                      : 'Пока никто не взял билет'}
                    {p.requires_phone_verification && <> · нужен телефон</>}
                  </div>
                  <button
                    className={afford && !maxed ? 'btn-primary px-3 py-2 text-sm' : 'btn-ghost px-3 py-2 text-sm'}
                    disabled={!afford || maxed || data.frozen || busy === p.id}
                    onClick={() => buy(p)}
                  >
                    {maxed ? 'Лимит' : busy === p.id ? '…' : `Билет за ${nfmt(p.ticket_price_coins)}`}
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <p className="mt-5 text-center text-xs leading-relaxed text-ash">
        Каждый билет — одна строка в списке участников. Списки выгружаются перед эфиром
        и не меняются после.{' '}
        <a className="text-turq underline" href={import.meta.env.VITE_RULES_URL ?? '#'}
           target="_blank" rel="noreferrer">Полные правила акции</a>
      </p>

      <button className="btn-ghost mt-5 w-full" onClick={onClose}>Назад</button>

      <Sheet open={needPhone} onClose={() => setNeedPhone(false)} title="Нужен телефон">
        <p className="mb-4 text-sm leading-relaxed text-ash">
          Билеты на дорогие призы выдаём только с подтверждённым номером — один номер на один
          аккаунт. Это защита от накрутки, а не сбор базы.
        </p>
        <button className="btn-primary w-full" onClick={() => {
          setNeedPhone(false);
          tg.requestContact
            ? tg.requestContact(async (ok, res) => {
              const phone = res?.responseUnsafe?.contact?.phone_number;
              if (!ok || !phone) return;
              const r = await api.post<{ ok: boolean; reason?: string }>('/me/phone', { phone });
              toast(r.ok ? 'Телефон подтверждён' : r.reason ?? 'Не вышло', r.ok ? 'ok' : 'bad');
            })
            : toast('Открой приложение из Telegram', 'bad');
        }}>
          Поделиться номером
        </button>
        <p className="mt-2 text-center text-xs text-ash">{me.phone_verified ? 'Номер уже подтверждён' : ''}</p>
      </Sheet>
    </div>
  );
}
